import { useMemo } from "react";
import { Field, BasicField, AddFieldModifierParams } from "../models/fields";
import { StateEnveloped } from "../redux/helpers";
import { makeHookDeprecationWarn } from "../helpers/warnings";

import {
  useFieldsData,
  useCreateField,
  useDeleteField,
  useFieldsGlobalActivity,
  useFieldsGlobalStatus,
} from "../next/hooks";

export type Data = {
  fields: Field[];
  fieldsById: Record<string, Field | undefined>;
  fieldsByFarmId: Record<string, Field[] | undefined>;
  activeFields: Field[];
};
export type FieldsHookResult = StateEnveloped<Data> & {
  modifiers: {
    addField: (field: AddFieldModifierParams) => Promise<BasicField>;
    removeField: (field: Field | BasicField) => Promise<void>;
  };
};
export type FieldsHook = () => FieldsHookResult;

const deprecationWarn = makeHookDeprecationWarn("useFields");

/**
 * @deprecated Use next.hooks.useFields instead.
 */
export const Hook: FieldsHook = () => {
  deprecationWarn();

  const data = useFieldsData();

  const status = useFieldsGlobalStatus();
  const activity = useFieldsGlobalActivity();

  const { mutate: createField } = useCreateField();
  const { mutate: deleteField } = useDeleteField();

  const modifiers = useMemo(
    () => ({
      addField: (field: AddFieldModifierParams): Promise<BasicField> => {
        return createField(field);
      },
      removeField: async (field: Field | BasicField): Promise<void> => {
        await deleteField(field.id);
      },
    }),
    [createField, deleteField],
  );

  const memoizedResult = useMemo(
    () => ({
      status,
      activity,
      data,
      modifiers,
    }),
    [status, activity, data, modifiers],
  );

  return memoizedResult;
};

export default Hook;
